import * as types from './types';
import { conf, Relationship, RelPoint, RelLink, RelSide, RelType } from 'src/config';
import _, { Dictionary } from 'lodash';

export const getMorph = (body: any) => {
    let morph = undefined;
    const href = _.get(body, ['_links', 'self', 'href']);
    if (href) {
        const regArr: RegExpExecArray | null = /\/api\/([^\/]+)\/\d+/.exec(href);
        if (regArr !== null) {
            morph = regArr[1];
        }
    }
    return morph;
}

export const getRelInfo: (vector: RelPoint) => {
    direct: boolean,
    name: string,
    prop: string,
    many: boolean,
    isM2M: boolean,
    isM21: boolean,
    is121: boolean,
} = (vector) => {
    const { rel } = vector;
    const direct = isDirect(vector);
    const name = direct ? rel.related : rel.resource;
    const prop = direct ? rel.referenceName : rel.backReferenceName;

    const many = isMany(vector);

    return {
        direct,
        name,
        prop,
        many,
        isM2M: rel.metaType === RelType.MANY_TO_MANY,
        isM21: rel.metaType === RelType.MANY_TO_ONE,
        is121: rel.metaType === RelType.ONE_TO_ONE,
    }
}

// does vector point to many side
export const isMany: (vector: RelPoint) => boolean = (vector) => {
    const { rel, side: dir } = vector;
    const direct = isDirect(vector);
    return rel.metaType === RelType.MANY_TO_MANY || (!direct && rel.metaType === RelType.MANY_TO_ONE);
}

export const isOfType: (id: string, entityType: types.EntityType) => boolean = (id, entityType) => {
    return getType(id) === entityType;
}

export const getType: (id: string) => types.EntityType = (id) => {
    if (id.startsWith('new:')) {
        return types.EntityType.Root;
    } else if (id.includes(')/')) {
        return types.EntityType.Temp;
    } else if (id.startsWith('-')) {
        return types.EntityType.Transient;
    } else if (parseInt(id).toString() === id) {
        return types.EntityType.Persistent;
    }
    throw new Error(`Unknown entity id type: "${id}"`);
}

export const isPersistent: (id: string) => boolean = (id) => isOfType(id, types.EntityType.Persistent);
export const isTransient: (id: string) => boolean = (id) => isOfType(id, types.EntityType.Transient);
export const isTemp: (id: string) => boolean = (id) => isOfType(id, types.EntityType.Temp);
export const isRoot: (id: string) => boolean = (id) => isOfType(id, types.EntityType.Root);

export const parseId: (id: string) => types.RelPort | null = (id) => {
    const arr = /([a-zA-Z][a-zA-Z\d]+)\((.+)\)\/([a-zA-Z][a-zA-Z\d]+)/g.exec(id);

    if (arr !== null) {
        const name = arr[1];
        const id = arr[2];
        const attrName = arr[3];

        const vector: RelPoint | undefined = conf.getRelVector(name, attrName);
        if (vector === undefined) {
            return null;
        }

        const many: boolean = [RelType.MANY_TO_MANY].includes(vector.rel.metaType);

        return {
            relPoint: vector,
            locator: {
                name,
                id,
            },
            attrName,
            many,
        }
    }

    return null;
}

export const parseTempId: (tempId: string, acc?: types.RelPort[]) => types.RelPort[] = (tempId, acc = []) => {
    const arr = /([a-zA-Z][a-zA-Z\d]+)\((.+)\)\/([a-zA-Z][a-zA-Z\d]+)/g.exec(tempId);

    if (arr !== null) {
        const name = arr[1];
        const id = arr[2];
        const attrName = arr[3];

        const vector: RelPoint | undefined = conf.getRelVector(name, attrName);

        if (vector !== undefined) {
            // const many: boolean = [RelType.MANY_TO_MANY].includes(vector.rel.metaType);

            acc.unshift({
                relPoint: vector,
                locator: {
                    name,
                    id,
                },
                attrName,
                // many,
            });

            return parseTempId(id, acc);
        }
    }

    return acc;
}

export const nextTransientId: (entities: Dictionary<types.Entity>) => string = (entities) => {
    const arr = _.keys(entities)
        .filter(id => parseInt(id).toString() === id)
        .map(id => parseInt(id))
        .filter(id => id < 0)
        .sort();


    const nextNum: number = arr.length > 0 ? arr.pop()! - 1 : -1;
    return nextNum.toString();
}

export const getLocator: (resourceName: string, entity: types.Entity) => types.Locator = (resourceName, entity) => {
    return {
        name: resourceName,
        id: (entity.attrs['_id'] ?? entity.values['_id']).toString(),
    }

}

export const isDirect: (vector: RelPoint) => boolean = (vector) => {
    return vector.side === RelSide.SOURCE;
}

export const toErrorTree: (errors: types.ErrorTree) => types.ErrorTree = (errors) => {
    const entityErrors = _.omitBy(errors, (value, key) => key !== '/');
    const fieldErrors = _.mapKeys(_.pickBy(errors, (value, key) => key !== '/'), (value, key) => key.substring(1));

    const rootErrors: types.ErrorTree = _.pickBy(fieldErrors, (value, key) => key.split('.').length === 1);
    const nestedErrors = _.omitBy(fieldErrors, (value, key) => key.split('.').length === 1);

    const nestedHeads = _.uniq(_.keys(nestedErrors).map(key => key.split('.')[0]));

    nestedHeads.reduce((acc, head) => {
        const headsErrors = _.mapKeys(
            _.pickBy(
                nestedErrors,
                (value, key) => key.startsWith(head + '.')
            ),
            (value, key) => '/' + key.substring(head.length + 1)
        );

        const name = getNameFromPropKey(head);
        const index = getIndexFromPropKey(head)!;

        const arr: types.ErrorTree[] = getOrSet(acc, name + "[]", []);

        arr[index] = toErrorTree(headsErrors);

        return acc;
    }, rootErrors);

    const tree = {};
    Object.assign(tree, rootErrors);
    Object.assign(tree, entityErrors);
    return tree;
}

const getIndexFromPropKey: (propKey: string) => number | undefined = (propKey) => {
    let index = undefined;
    const bracketIndex: number = propKey.indexOf('[');
    if (bracketIndex > -1) {
        index = parseInt(propKey.substring(bracketIndex + 1, propKey.length));
    }
    return index;
}

const getNameFromPropKey: (propKey: string) => string = (propKey) => {
    let name = propKey;
    const bracketIndex: number = propKey.indexOf('[');
    if (bracketIndex > -1) {
        name = propKey.substring(0, bracketIndex);
    }
    return name;
}

const getOrSet: <T>(obj: any, name: string, orVal: T) => T = (obj, name, orVal) => {
    const value = obj[name];
    if (value === undefined) {
        obj[name] = orVal;
        return orVal;
    }
    return value;
}

export const entity: (id?: string) => types.Entity = (id) => {
    const entity: types.Entity = {
        attrs: {},
        values: {},
        errors: {},
        locked: false,
    };
    if (id != null) {
        entity.values['_id'] = id;
    }
    return entity;
}

export type InitializedEntityType = 'transient' | 'persistent';
export type InitializedPorts = Record<InitializedEntityType, types.RelPort[]>;


export const toInitializedPorts: (relPoint: RelPoint, entity: types.Entity) => InitializedPorts = (relPoint, entity) => {
    const { rel, side, rel: { metaType } } = relPoint;

    const isMany: boolean = metaType === RelType.MANY_TO_MANY || (metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
    const attrName = relPoint.side === RelSide.SOURCE ? rel.referenceName : rel.backReferenceName;
    const name = relPoint.side === RelSide.SOURCE ? rel.related : rel.resource;

    let relIds: types.ID[] = [];
    const genericValue: any = entity.values[attrName];

    if (isMany && genericValue != null) {
        relIds = genericValue as types.ID[];
    } else if (genericValue != null) {
        relIds = [genericValue as types.ID];
    }

    const initializedPorts: InitializedPorts = {
        'transient': [],
        'persistent': [],
    };

    relIds.map(id => id.toString()).forEach(id => {
        let portType: InitializedEntityType;
        if (isPersistent(id)) {
            portType = 'persistent';
        } else if (isTransient(id)) {
            portType = 'transient';
        } else {
            throw new Error('unsupported entity id: ' + id);
        }
        initializedPorts[portType].push({
            relPoint,
            attrName,
            locator: {
                name,
                id,
            },
        })
    });

    return initializedPorts;
}

export const toInitializedPortsMixed: (relPoint: RelPoint, entity: types.Entity) => types.RelPort[] = (relPoint, entity) => {
    const { rel, side, rel: { metaType } } = relPoint;

    const isMany: boolean = metaType === RelType.MANY_TO_MANY || (metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
    const attrName = relPoint.side === RelSide.SOURCE ? rel.referenceName : rel.backReferenceName;
    const name = relPoint.side === RelSide.SOURCE ? rel.related : rel.resource;

    let relIds: types.ID[] = [];
    const genericValue: any = entity.values[attrName];

    if (isMany && genericValue != null) {
        relIds = genericValue as types.ID[];
    } else if (genericValue != null) {
        relIds = [genericValue as types.ID];
    }

    const ports: types.RelPort[] = [];

    relIds.map(id => id.toString()).forEach(id => {
        ports.push({
            relPoint,
            attrName,
            locator: {
                name,
                id,
            },
        })
    });

    return ports;
}

export const getPorts: (entity: types.Entity, name: string) => types.RelPort[] = (entity, name) => {
    const allPorts: types.RelPort[] = [];
    conf.getVectors(name).forEach(vector => toInitializedPortsMixed(vector, entity).forEach(port => allPorts.push(port)));
    return allPorts;
}

export const isEqualRel: (rel1: Relationship, rel2: Relationship) => boolean = (rel1, rel2) => {
    return rel1.resource === rel2.resource
        && rel1.related === rel2.related
        && rel1.referenceName === rel2.referenceName
        && rel1.backReferenceName === rel2.backReferenceName
        && rel1.metaType === rel2.metaType;
}

export const isEqualLocator: (locator1: types.Locator, locator2: types.Locator) => boolean = (locator1, locator2) => {
    return locator1.name == locator2.name && locator1.id.toString() == locator2.id.toString();
}

export const isEqualLink: (link1: RelLink, link2: RelLink) => boolean = (link1, link2) => {
    const { rel: rel1, resource: source1, related: target1 } = link1;
    const { rel: rel2, resource: source2, related: target2 } = link2;
    return isEqualRel(rel1, rel2) && isEqualLocator(source1, source2) && isEqualLocator(target1, target2);
}

const getTypedIds: (vector: RelPoint, value: types.Value) => types.ID[] = (vector, value) => {
    const { rel, side } = vector;
    const many: boolean = (rel.metaType === RelType.MANY_TO_MANY) || (rel.metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
    return many ?
        ((value || []) as types.ID[]).map(id => id.toString())
        :
        value != null ? [value.toString()] : [];
}

export const getOrigAndNewIds: (
    entities: types.Entities,
    locator: types.Locator,
    vector: RelPoint,
    ids?: string[]
) => { 'original': string[], 'new': string[] } = (
    entities,
    locator,
    vector,
    ids
) => {
        const { rel, side } = vector;
        const many: boolean = (rel.metaType === RelType.MANY_TO_MANY) || (rel.metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
        const { direct, name, prop, isM2M, isM21 } = getRelInfo(vector);

        const entity: types.Entity = locateEntity(entities, locator);


        let origIds: types.ID[];
        let newIds: types.ID[];

        if (ids == null) {
            origIds = getTypedIds(vector, _.get(entity.attrs, [prop]));
            newIds = getTypedIds(vector, _.get(entity.values, [prop]));
        } else {
            origIds = getTypedIds(vector, _.get(entity.values, [prop]));
            newIds = ids;
        }

        return { 'original': origIds, 'new': newIds };
    }

export const getOrigAndRemovedIds: (
    entities: types.Entities,
    locator: types.Locator,
    vector: RelPoint,
    ids?: string[]
) => { 'original': string[], 'removed': string[] } = (
    entities,
    locator,
    vector,
    ids
) => {
        const { rel, side } = vector;
        const many: boolean = (rel.metaType === RelType.MANY_TO_MANY) || (rel.metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
        const { direct, name, prop, isM2M, isM21 } = getRelInfo(vector);
        const entity: types.Entity = locateEntity(entities, locator);

        let origIds: types.ID[];
        let newIds: types.ID[];
        let removedIds: types.ID[];

        if (ids == null) {
            origIds = getTypedIds(vector, _.get(entity.attrs, [prop]));
            newIds = getTypedIds(vector, _.get(entity.values, [prop]));
            removedIds = origIds.filter(id => !newIds.includes(id));
        } else {
            origIds = getTypedIds(vector, _.get(entity.values, [prop]));
            removedIds = ids.filter(id => origIds.includes(id));
        }

        return { 'original': origIds, 'removed': removedIds };
    }

export const locateBucket: (
    entities: types.Entities,
    locator: types.Locator
) => Dictionary<types.Entity> = (
    entities,
    locator
) => {
        let name: string | undefined = locator.name;
        const id = locator.id;
        let bucket = entities[name];
        while (bucket == null || bucket[id] == null) {
            name = conf.getSuperName(name);
            if (name == null) {
                throw 'Cannot locate entity';
            }
            bucket = entities[name];
        }
        return bucket;
    }


export const locateEntity: (
    entities: types.Entities,
    locator: types.Locator
) => types.Entity = (
    entities,
    locator
) => {
        const bucket: Dictionary<types.Entity> = locateBucket(entities, locator);
        return bucket[locator.id];
    }

export const toRelLink: (port: types.RelPort, counterpart: types.Locator) => RelLink = (port, counterpart) => {
    const { relPoint: { side, rel }, locator } = port;
    return {
        rel,
        resource: side == RelSide.SOURCE ? counterpart : locator,
        related: side == RelSide.TARGET ? counterpart : locator,
    }
}

export const isPortOfLink: (port: types.RelPort, relLink: RelLink) => boolean = (port, relLink) => {
    const { relPoint: { side, rel: portRel }, locator: portLocator } = port;
    const { rel: linkRel, resource, related } = relLink;
    const matchRel = isEqualRel(portRel, linkRel);
    const matchLocator = isEqualLocator(portLocator, side == RelSide.SOURCE ? related : resource);
    return matchRel && matchLocator;
}

export const isLocatorOfLink: (locator: types.Locator, relLink: RelLink) => boolean = (locator, relLink) => {
    const { resource, related } = relLink;
    return isEqualLocator(locator, resource) || isEqualLocator(locator, related);
}

export const toOppositeVector: (vector: RelPoint) => RelPoint = (vector) => {
    const { rel, side } = vector;
    return {
        rel,
        side: side === RelSide.SOURCE ? RelSide.TARGET : RelSide.SOURCE
    }
}


export const inError: (
    entities: types.Entities,
    locator: types.Locator,
    visited?: RelLink[]

) => boolean = (entities, locator, visited = []) => {
    const entity = locateEntity(entities, locator);

    // self errors
    if (_.keys(entity.errors).length > 0) {
        return true;
    }

    // 'children' errors
    const vectors: RelPoint[] = conf.getVectors(locator.name);

    for (const vector of vectors) {
        const ports: types.RelPort[] = toInitializedPortsMixed(vector, entity)
            .filter(port => visited.find(relLink => isLocatorOfLink(port.locator, relLink)) == null);

        const errored = ports.find(port => {
            const related: types.Entity = locateEntity(entities, port.locator);
            return _.keys(related.errors).length > 0;
        });
        if (errored != null) {
            return true;
        }
    }

    // recursion
    for (const vector of vectors) {
        const ports: types.RelPort[] = toInitializedPortsMixed(vector, entity)
            .filter(
                port => visited.find(relLink => isLocatorOfLink(port.locator, relLink)) == null
            );

        ports.forEach(port => visited.push(toRelLink(port, locator)));

        const errored = ports.find(port => inError(entities, port.locator, visited));
        if (errored != null) {
            return true;
        }
    }

    return false;
}